/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, Coins, Award, Gem, Sparkles, Lock, Crown, Star, Shield } from "lucide-react";

/**
 * Plans — the tier comparison + loyalty perks page.
 *
 * Renamed tiers (display only — backend IDs stay free/plus/premium so Stripe
 * products and subscription docs don't break):
 *   free    → Bronze    (Award icon, copper color)
 *   plus    → Gold      (Award icon, gold color)
 *   premium → Diamond   (Gem icon, cyan color)
 *
 * Reachable at /account/plans. /subscribe and /my-subs both redirect here.
 */

const TIERS = [
  {
    id: "free",
    display: "BRONZE",
    tagline: "Everything you need to hang out.",
    emblem: Award,
    accent: "#C08457",         // copper
    accentSoft: "rgba(192,132,87,0.12)",
    price: null,
    perks: {
      "Core": [
        "Create your profile",
        "Follow creators",
        "Join communities",
        "Watch streams + chat",
        "Post updates + join LFG",
        "Basic friend list",
        "Earn Gold through activity",
        "Participate in giveaways",
        "Basic achievements",
      ],
    },
    cta: "Current Plan",
  },
  {
    id: "plus",
    display: "GOLD",
    tagline: "The power-user tier.",
    emblem: Award,
    accent: "#FFD700",         // gold
    accentSoft: "rgba(255,215,0,0.12)",
    price: { gc: 500, usd: 5 },
    perks: {
      "Profile": [
        "Animated profile banner",
        "Extra profile themes",
        "More profile badges",
        "Featured profile sections",
        "Longer bio + more social links",
      ],
      "Community": [
        "Create more communities",
        "Create private communities",
        "Extra moderation tools",
        "Schedule community events",
      ],
      "Visibility": [
        "Higher placement in Discover",
        "Priority in LFG",
        "Profile featured more often",
      ],
      "Storage & Social": [
        "More media uploads · larger file limits",
        "Custom reactions + extra emojis",
        "More friends · larger block list",
      ],
      "Creator": [
        "Creator analytics",
        "Schedule posts",
        "Auto-post announcements",
      ],
      "Rewards": [
        "+5% GC bonus on all coin-pack purchases",
        "Exclusive Gold badge",
        "Monthly Gold-only giveaways",
        "Exclusive cosmetics",
      ],
    },
    highlight: true,
    cta: "Upgrade to Gold",
  },
  {
    id: "premium",
    display: "DIAMOND",
    tagline: "VIP. Everything unlocked.",
    emblem: Gem,
    accent: "#00E5FF",         // cyan / diamond
    accentSoft: "rgba(0,229,255,0.12)",
    price: { gc: 1000, usd: 10 },
    perks: {
      "Everything in Gold, plus…": [
        "+15% GC bonus on all coin-pack purchases",
      ],
      "Premium Profile": [
        "Animated avatar + borders",
        "Premium profile cards",
        "Dynamic backgrounds",
        "Video profile headers",
        "Exclusive profile effects",
      ],
      "Visibility": [
        "Highest Discover priority",
        "Featured creator rotation",
        "LFG + community priority",
      ],
      "AI Features": [
        "Unlimited AI profile assistant",
        "AI banner generation",
        "AI post rewriting",
        "AI moderation suggestions",
        "AI clip titles + descriptions",
      ],
      "Creator Tools": [
        "Advanced analytics + follower insights",
        "Revenue dashboard + link click tracking",
        "Custom landing pages",
        "Pinned media carousel",
        "Premium creator badge",
      ],
      "Community": [
        "Unlimited community creation",
        "Advanced permissions + themes",
        "Community verification + analytics",
      ],
      "Cosmetics & Rewards": [
        "Animated name color + chat badge",
        "Premium fonts + seasonal cosmetics",
        "Profile music (optional)",
        "Monthly mystery crate",
        "Premium-only events",
      ],
      "Communication": [
        "Priority support",
        "Early beta access · vote on new features",
        "Private Discord + AMA sessions",
      ],
    },
    cta: "Upgrade to Diamond",
  },
];

// Which perks are already live vs coming soon. Anything not listed as `live`
// is displayed with a subtle "Soon" chip so we never over-promise.
const LIVE_PERKS = new Set([
  // Bronze core (all live)
  "Create your profile", "Follow creators", "Join communities",
  "Watch streams + chat", "Post updates + join LFG", "Basic friend list",
  "Earn Gold through activity", "Participate in giveaways", "Basic achievements",
  // Gold live
  "+5% GC bonus on all coin-pack purchases",
  "Exclusive Gold badge",
  "Create more communities",
  "Create private communities",
  "Creator analytics",
  // Diamond live
  "+15% GC bonus on all coin-pack purchases",
]);

function isLivePerk(text) { return LIVE_PERKS.has(text); }

export default function Plans() {
  const { user, loading, refreshMe } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const streamer = params.get("streamer") || "";
  const [busy, setBusy] = useState("");
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    // Wait for auth hydration before redirecting — otherwise a race sends
    // authenticated visitors to /login on hard refresh.
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/subscriptions/loyalty").then((r) => setLoyalty(r.data)).catch(() => setLoyalty(null));
  }, [user]);

  const buy = async (tier) => {
    if (!user) return nav("/login");
    if (tier === "free") return;
    setBusy(tier);
    try {
      const r = await api.post("/subscriptions/purchase", {
        tier,
        streamer_username: streamer || null,
      });
      const displayName = TIERS.find(t => t.id === tier)?.display || tier;
      toast.success(`${displayName} activated!`, {
        description: "Your coin-pack purchase bonus is now live. Cosmetics + loyalty perks unlock as they roll out.",
        duration: 5000,
      });
      if (refreshMe) await refreshMe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
    } finally {
      setBusy("");
    }
  };

  if (!user) return null;

  const currentTier = user.tier || "free";
  const goldCoins = user.gold_coins ?? 0;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="plans-page">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="sc-label" style={{ color: "var(--sc-pink)" }}>◢ PLANS</div>
        <h1 className="sc-heading text-4xl sm:text-5xl font-black mt-2">Pick your tier.</h1>
        <p className="text-sm text-zinc-400 max-w-2xl mx-auto mt-3">
          Pay with Gold Coins. Your tier gives you a bonus on every coin pack you buy, plus badges + cosmetics that can't be bought again.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 chip-neon volt" data-testid="plans-balance">
          <Coins className="w-3.5 h-3.5" /> {goldCoins.toLocaleString()} Gold Coins
        </div>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TIERS.map((t) => (
          <TierCard
            key={t.id}
            tier={t}
            isCurrent={currentTier === t.id}
            busy={busy === t.id}
            onBuy={() => buy(t.id)}
          />
        ))}
      </div>

      {/* Loyalty perks */}
      <LoyaltySection loyalty={loyalty} />

      {/* Footer note */}
      <p className="text-center text-[11px] text-zinc-600 mt-10 max-w-2xl mx-auto">
        Some perks marked <em>Soon</em> are on the roadmap and not yet active. They'll unlock automatically for your tier once released — no re-subscription needed.
      </p>
    </div>
  );
}

function TierCard({ tier, isCurrent, busy, onBuy }) {
  const Emblem = tier.emblem;
  const canDowngrade = !isCurrent && tier.id === "free";
  return (
    <div
      className={`relative sc-card p-6 flex flex-col ${tier.highlight ? "ring-2" : ""}`}
      style={{
        borderColor: isCurrent ? tier.accent : undefined,
        background: `linear-gradient(180deg, ${tier.accentSoft} 0%, transparent 70%), #0d0d10`,
        ringColor: tier.highlight ? tier.accent : undefined,
      }}
      data-testid={`plan-card-${tier.id}`}
    >
      {tier.highlight && (
        <div className="absolute top-3 right-3 text-[9px] font-black tracking-widest px-2 py-0.5 rounded-full z-10" style={{ background: tier.accent, color: "#000" }}>
          MOST POPULAR
        </div>
      )}
      {isCurrent && (
        <div className="absolute top-3 left-3 text-[9px] sc-mono uppercase tracking-widest px-2 py-0.5 border rounded z-10" style={{ color: tier.accent, borderColor: tier.accent, background: "rgba(0,0,0,0.6)" }}>
          Current
        </div>
      )}

      {/* Emblem + name */}
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: tier.accentSoft, border: `2px solid ${tier.accent}` }}
        >
          <Emblem className="w-6 h-6" style={{ color: tier.accent }} />
        </div>
        <div>
          <div className="sc-heading text-2xl font-black" style={{ color: tier.accent }}>{tier.display}</div>
          <div className="text-[11px] text-zinc-500">{tier.tagline}</div>
        </div>
      </div>

      {/* Price */}
      <div className="mb-4">
        {tier.price ? (
          <div className="flex items-baseline gap-1.5">
            <span className="sc-heading text-3xl font-black">{tier.price.gc.toLocaleString()}</span>
            <span className="text-xs text-zinc-500 sc-mono">GC / mo</span>
            <span className="text-[10px] text-zinc-600 sc-mono ml-1">(≈ ${tier.price.usd})</span>
          </div>
        ) : (
          <div className="sc-heading text-3xl font-black text-zinc-400">FREE</div>
        )}
      </div>

      {/* Perks grouped by category */}
      <div className="flex-1 space-y-4 mb-6">
        {Object.entries(tier.perks).map(([group, items]) => (
          <div key={group}>
            <div className="text-[9px] sc-mono uppercase tracking-widest text-zinc-500 mb-1.5">{group}</div>
            <ul className="space-y-1.5">
              {items.map((perk) => {
                const live = isLivePerk(perk);
                return (
                  <li key={perk} className="flex items-start gap-2 text-xs" data-testid={`plan-${tier.id}-perk`}>
                    <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color: tier.accent }} />
                    <span className={live ? "text-zinc-300" : "text-zinc-400"}>{perk}</span>
                    {!live && (
                      <span className="ml-auto text-[8px] sc-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-zinc-700 text-zinc-500 shrink-0">Soon</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* CTA */}
      {isCurrent ? (
        <button className="sc-btn-ghost text-sm w-full" disabled data-testid={`plan-cta-${tier.id}`}>
          Current Plan
        </button>
      ) : canDowngrade ? (
        <button className="sc-btn-ghost text-sm w-full" disabled data-testid={`plan-cta-${tier.id}`}>
          Cancel from Gold/Diamond to downgrade
        </button>
      ) : (
        <button
          onClick={onBuy}
          disabled={busy}
          className="sc-btn text-sm w-full flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: tier.accent, color: "#000" }}
          data-testid={`plan-cta-${tier.id}`}
        >
          {busy ? "Activating…" : (<>{tier.cta} <Coins className="w-4 h-4" /></>)}
        </button>
      )}
    </div>
  );
}

function LoyaltySection({ loyalty }) {
  if (!loyalty) return null;
  const months = loyalty.months || 0;
  const milestones = loyalty.all_milestones || [];
  const unlockedIds = new Set((loyalty.unlocked || []).map(m => m.id));

  return (
    <div className="mt-12 sc-card p-6" data-testid="loyalty-section">
      <div className="flex items-center gap-2 mb-4">
        <Star className="w-4 h-4 text-[#FFD700]" />
        <div className="sc-label" style={{ color: "#FFD700" }}>LOYALTY PERKS</div>
      </div>
      <h3 className="sc-heading text-xl font-bold">
        {months > 0 ? `You've been subscribed ${months} ${months === 1 ? "month" : "months"}` : "Stay subscribed to unlock exclusive cosmetics"}
      </h3>
      <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
        Loyalty rewards can't be bought — they're earned by staying with us. They're purely cosmetic and never gate features or safety.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        {milestones.map(m => {
          const unlocked = unlockedIds.has(m.id);
          return (
            <div
              key={m.id}
              className="p-4 border rounded-md flex items-start gap-3"
              style={{
                borderColor: unlocked ? "#FFD700" : "rgba(255,255,255,0.1)",
                background: unlocked ? "rgba(255,215,0,0.05)" : "rgba(0,0,0,0.2)",
              }}
              data-testid={`loyalty-milestone-${m.id}`}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: unlocked ? "#FFD70022" : "rgba(255,255,255,0.05)", border: `2px solid ${unlocked ? "#FFD700" : "rgba(255,255,255,0.1)"}` }}
              >
                {unlocked ? <Crown className="w-5 h-5 text-[#FFD700]" /> : <Lock className="w-4 h-4 text-zinc-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs sc-mono text-zinc-500">{m.months} months</div>
                <div className="text-sm font-bold" style={{ color: unlocked ? "#FFD700" : "#fff" }}>{m.label}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{m.reward}</div>
              </div>
            </div>
          );
        })}
      </div>

      {loyalty.next_milestone && (
        <div className="mt-4 text-[11px] sc-mono text-zinc-500 text-center">
          Next unlock: <span className="text-white">{loyalty.next_milestone.label}</span> at {loyalty.next_milestone.months} months.
        </div>
      )}
    </div>
  );
}
